import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../parts/SvgIcon";
import { useRillContext } from "../../../../rill/RillReact/share/base";
import { isFemaleCycleSectionEnabledFromConfig } from "../../../../utils/client-configuration-helpers";
import { CHECKIN } from "../../../coaching-portal/tasks/task-constants";
import NavigationSections from "./NavigationSections";

const linksArrStats = [
  { route: "/metrics/readiness-rate", metricKey: "readinessRate" },
  {
    route: "/metrics/accountability",
    metricKey: "accountabilityTracker",
    key: "accountability",
  },
  { route: "/metrics/session-rpe", metricKey: "sessionRpe" },
  { route: "/metrics/bodyweight", metricKey: "bodyweight" },
  { route: "/metrics/step-count", metricKey: "stepCount" },
  { route: "/metrics/sleep-time", metricKey: "sleepTime" },
  { route: "/metrics/water-intake", metricKey: "waterIntake" },
  { route: "/metrics/hrv", metricKey: "hrv" },
  { route: "/metrics/body-measurements", metricKey: "bodyMeasurements" },
  {
    route: "/metrics/female-cycle",
    metricKey: "menstrualCycleTracking",
    key: "femaleCycle",
  },
  { route: "/metrics/progress-photos", metricKey: "progressPhotos" },
  { route: "/metrics/meal-photos", metricKey: "mealPhotos" },
  { route: "/metrics/body-composition", metricKey: "bodyComposition" },
  { route: "/metrics/body-fat", metricKey: "bodyFat" },
  { route: "/metrics/resting-heart-rate", metricKey: "restingHeartRate" },
  {
    route: "/metrics/check-in-summary",
    metricKey: "checkInSummary",
    key: "checkInSummary",
  },
];

const linksArrTraining = [
  { route: "/metrics/exercise-history", metricKey: "exerciseHistory" },
  {
    route: "/metrics/block-review",
    metricKey: "blockReview",
    key: "blockReview",
  },
  // Temporary redirect for coaches still looking for Annual Planner under
  // Reports - remove once they've adjusted to its new Profile location.
  {
    route: "/profile",
    key: "periodizationMoved",
  },
  { route: "/metrics/video-list", metricKey: "videoList" },
  { route: "/metrics/rpe-report", metricKey: "rpeTable", key: "rpeTable" },
  { route: "/metrics/personal-record", metricKey: "personalRecords" },
  { route: "/metrics", metricKey: "strengthMetrics", key: "strengthMetrics" },
];

const NavigationComponent = ({
  query,
  pathName = "",
  text,
  textKey,
  classNames = "",
}) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const { locals } = useRillContext();
  const backLabel = textKey ? t(textKey) : text || t("nav.calendar");

  const organizationCoach = query.organizationCoach || undefined;
  const [webViewState, setWebViewState] = useState();
  const { athlete, date = null, view = null, navFrom = null } = query;
  const { activeUser, organizationOwner } = locals;

  // Filter links based on athlete task availability
  const filteredLinksArrStats = linksArrStats.filter((link) => {
    if (link.key === "femaleCycle") {
      return isFemaleCycleSectionEnabledFromConfig(locals.clientConfiguration);
    }
    return true;
  });

  // The Periodization redirect only makes sense when viewing a specific
  // athlete's reports (it links to that athlete's Profile tab).
  const filteredLinksArrTraining = linksArrTraining.filter((link) => {
    if (link.key === "periodizationMoved") return !!athlete;
    return true;
  });

  const urlStart = athlete ? "/athlete-portal" : "/dashboard";
  const organizationCoachParam = organizationCoach
    ? `&organizationCoach=${organizationCoach}`
    : "";
  const athleteParam = athlete ? `&athlete=${athlete}` : "";
  const urlEnd = athleteParam + organizationCoachParam;
  const calendarUrlStart = athlete
    ? "/athlete-portal/calendar"
    : "/dashboard/calendar";
  const pathNameWithoutEndingSlash =
    pathName[pathName.length - 1] === "/"
      ? pathName.slice(0, pathName.length - 1)
      : pathName;

  useEffect(() => {
    setWebViewState(window.ReactNativeWebView);
  }, []);

  if (webViewState) return null;

  return (
    <aside role="secondary-navbar" className={`navigation-panel ${classNames}`}>
      {!!view && (
        <a
          href={`${calendarUrlStart}?view=day&date=${date}${urlEnd}`}
          className="link"
        >
          <SvgIcon src="/img/new-icons/back-button.svg" />
          <span>{backLabel}</span>
        </a>
      )}
      {!!navFrom && (
        <a
          href={`/coaching-portal/${
            navFrom === "tasks" ? `${navFrom}?tab=${CHECKIN}` : navFrom
          }`}
          className="link"
        >
          <img src="/img/new-icons/back-button.svg" alt={t("common:back")} />
          {navFrom}
        </a>
      )}

      <ul className="navigation-sections">
        <span>{t("metrics.bodyStats")}</span>
        <NavigationSections
          linksArr={filteredLinksArrStats}
          activeUser={activeUser}
          organizationOwner={organizationOwner}
          pathNameWithoutEndingSlash={pathNameWithoutEndingSlash}
          urlStart={urlStart}
          view={view}
          date={date}
          urlEnd={urlEnd}
        />
        <span>{t("metrics.training")}</span>
        <NavigationSections
          linksArr={filteredLinksArrTraining}
          activeUser={activeUser}
          organizationOwner={organizationOwner}
          pathNameWithoutEndingSlash={pathNameWithoutEndingSlash}
          urlStart={urlStart}
          view={view}
          date={date}
          urlEnd={urlEnd}
        />
      </ul>
    </aside>
  );
};

export default NavigationComponent;
